/**
 * What people actually wrote, paginated and apart from the aggregates.
 *
 * It is the only place the free text travels: `GET /:id/results` reports how many people wrote
 * something, never what. Two sources land in the same list — the answer to an open question
 * (`ANSWER`) and the box behind an "Outro: ___" option (`OTHER_TEXT`) — and the second one is
 * worth its own tag, because it is where the option list turns out to have been missing something.
 *
 * Only submitted responses are here (R14): half a sentence in an abandoned response is not an
 * opinion anyone chose to hand over.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTLoading } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { SurveyComment, SurveyResultsFilter } from '../../../Types/Surveys.types';
import { cutBaseSentence } from '../../../Utils/resultsCut';
import { COMMENT_SOURCE_LABELS, formatDate, languageName } from '../../../Utils/surveyFormat';
import { Pagination } from '@/Shared/components/Pagination/Pagination';
import { StatusTag } from '@/Shared/components/StatusTag/StatusTag';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
`;

const filtersStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
`;

const selectStyle = css`
  height: 36px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-xs);
  min-width: 220px;
`;

const countStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const listStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

const commentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-m);
  background: var(--color-surface);
`;

const metaStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  flex-wrap: wrap;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const monoStyle = css`
  font-family: var(--font-mono);
`;

const textStyle = css`
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`;

const emptyStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
`;

export interface ResultsCommentsProps {
  comments: SurveyComment[];
  total: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  /** `''` means every question with text. */
  questionKey: string;
  /** The text-bearing questions of this version, for the filter. */
  questionOptions: { key: string; label: string }[];
  /**
   * The cut these comments came back under, when there is one (R49).
   *
   * `questionKey` and the cut narrow different things and combine: one says **which question** is
   * in the feed, the other says **whose** text it is. So the count beside the picker has to name
   * the group, or a feed of six sentences out of a hundred and twelve reads as a bug.
   */
  cut?: SurveyResultsFilter;
  onQuestionKeyChange: (key: string) => void;
  onPageChange: (page: number) => void;
}

export const ResultsComments = ({
  comments,
  total,
  page,
  pageSize,
  isLoading,
  questionKey,
  questionOptions,
  cut,
  onQuestionKeyChange,
  onPageChange,
}: ResultsCommentsProps): JSX.Element => (
  <div className={wrapStyle}>
    <div className={filtersStyle}>
      <select
        id='survey-comments-question'
        className={selectStyle}
        value={questionKey}
        aria-label='Filtrar comentários por pergunta'
        onChange={event => {
          onQuestionKeyChange(event.target.value);
        }}
      >
        <option value=''>Todas as perguntas</option>
        {questionOptions.map(option => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
      <span className={countStyle}>
        {cut
          ? `${total} comentários nesta versão, ${cutBaseSentence(cut)}`
          : `${total} comentários nesta versão`}
      </span>
    </div>

    {isLoading && comments.length === 0 ? (
      <UTLoading type='dots' />
    ) : comments.length === 0 ? (
      <p id='survey-comments-empty' className={emptyStyle}>
        {cut
          ? `Ninguém desse recorte escreveu nada aqui. O recorte é ${cutBaseSentence(cut)}, e nenhuma delas deixou texto.`
          : 'Ninguém escreveu nada aqui ainda.'}
      </p>
    ) : (
      <div id='survey-comments-list' className={listStyle}>
        {comments.map(comment => (
          <article key={`${comment.responseId}-${comment.questionKey}`} className={commentStyle}>
            <div className={metaStyle}>
              <span className={monoStyle}>{comment.platformUserId}</span>
              <span>·</span>
              <span>{comment.questionLabel}</span>
              <span>·</span>
              <span>{languageName(comment.language)}</span>
              <span>·</span>
              <span>{formatDate(comment.submittedAt)}</span>
              <StatusTag status={comment.source === 'OTHER_TEXT' ? 'info' : 'neutral'} size='sm'>
                {COMMENT_SOURCE_LABELS[comment.source]}
              </StatusTag>
            </div>
            <span className={textStyle}>{comment.text}</span>
          </article>
        ))}
      </div>
    )}

    {total > pageSize ? (
      <div className={footerStyle}>
        <Pagination
          id='survey-comments-pagination'
          page={page}
          pageSize={pageSize}
          total={total}
          onChange={onPageChange}
        />
      </div>
    ) : null}
  </div>
);
